import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { Apple, ArrowBack, CardMembership, PhoneAndroid } from '@mui/icons-material';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionsAPI } from '../services/api';
import { formatAuditDate, formatEntitlementLabel, formatStoreLabel, getEffectiveRenewalStatus } from '../utils/adminDisplay';
import { tokens } from '../theme';

export default function BillingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, previewMode } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // /billing is a PrivateRoute like /dashboard, reachable directly by a
    // preview visitor (e.g. from the Settings "Open billing" button) with no
    // tokens. Without this guard the request 401s and the auth interceptor
    // bounces the preview session to /login. previewMode comes from
    // AuthContext (captured once at session establishment), not re-derived
    // from window.location.search, which in-app navigation rewrites without
    // the query string.
    if (!user || previewMode) {
      setLoading(false);
      return;
    }

    const loadSubscription = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await subscriptionsAPI.getMe();
        if (response.data?.success) {
          setSubscription(response.data.data);
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load subscription');
      } finally {
        setLoading(false);
      }
    };

    loadSubscription();
  }, [previewMode, user]);

  if (!user) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', px: 3 }}>
        <Stack spacing={1} sx={{ textAlign: 'center', maxWidth: 420 }}>
          <Typography variant="h5" sx={{ color: tokens.cream }}>
            Restoring your billing workspace...
          </Typography>
          <Typography sx={{ color: tokens.textMuted }}>
            If your session needs attention, FastTime will guide you back to sign in without dropping your place.
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress sx={{ color: tokens.accent }} />
      </Box>
    );
  }

  const isPremium = subscription?.isPremium === true;
  const expiresAt = subscription?.expiresAt ? new Date(subscription.expiresAt) : null;
  const trialEnd = subscription?.trialEnd ? new Date(subscription.trialEnd) : null;
  const renewalStatus = getEffectiveRenewalStatus(subscription);
  const auditRows = [
    ['Entitlement', formatEntitlementLabel(subscription)],
    ['Renewal status', renewalStatus],
    ['Trial', subscription?.trialStatus || (subscription?.isTrialActive ? 'active' : 'inactive')],
    ['Billing period', subscription?.billingPeriod || 'unknown'],
    ['Store', formatStoreLabel(subscription)],
    ['Last verified', formatAuditDate(subscription?.lastVerifiedAt)],
    ['Grace ends', formatAuditDate(subscription?.gracePeriodEndsAt)],
  ];

  return (
    <Box sx={{ minHeight: '100vh', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        <Button startIcon={<ArrowBack />} onClick={() => navigate({ pathname: '/dashboard', search: location.search })} sx={{ mb: 4 }}>
          Back to dashboard
        </Button>

        <Box sx={{ mb: 5 }}>
          <Typography variant="h2" sx={{ color: tokens.cream, fontSize: { xs: '2.7rem', md: '3.6rem' }, mb: 1.5 }}>
            Subscription & billing
          </Typography>
          <Typography sx={{ color: tokens.textSoft, maxWidth: 620 }}>
            Review your FastTime plan, renewal timeline, and where subscription changes are managed.
          </Typography>
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mb: 4, bgcolor: 'rgba(230,124,115,0.08)', color: '#f3b0aa' }}>
            {error}
          </Alert>
        ) : null}

        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 58,
                        height: 58,
                        borderRadius: 4,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: isPremium ? 'rgba(169,191,122,0.12)' : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${isPremium ? 'rgba(169,191,122,0.26)' : tokens.border}`,
                      }}
                    >
                      <CardMembership sx={{ color: isPremium ? tokens.accentStrong : tokens.textMuted, fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="h4" sx={{ color: tokens.cream, fontSize: '2rem', mb: 0.5 }}>
                        FastTime Premium
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip
                          label={formatEntitlementLabel(subscription)}
                          sx={{
                            bgcolor: isPremium ? 'rgba(169,191,122,0.12)' : 'rgba(255,255,255,0.05)',
                            color: isPremium ? tokens.accentStrong : tokens.textSoft,
                            border: `1px solid ${isPremium ? 'rgba(169,191,122,0.26)' : tokens.border}`,
                            textTransform: 'capitalize',
                          }}
                        />
                        <Chip
                          label={renewalStatus}
                          variant="outlined"
                          sx={{ textTransform: 'capitalize', borderColor: tokens.border, color: tokens.textSoft }}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                  <Typography sx={{ color: tokens.textMuted }}>Wellness membership</Typography>
                </Stack>

                <Grid container spacing={2.5} sx={{ mt: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2.5, borderRadius: 4, border: `1px solid ${tokens.border}`, bgcolor: 'rgba(255,255,255,0.02)' }}>
                      <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                        Renewal date
                      </Typography>
                      <Typography variant="h6" sx={{ color: tokens.cream, mt: 1 }}>
                        {expiresAt ? format(expiresAt, 'PPP') : 'Not scheduled'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2.5, borderRadius: 4, border: `1px solid ${tokens.border}`, bgcolor: 'rgba(255,255,255,0.02)' }}>
                      <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                        Trial status
                      </Typography>
                      <Typography variant="h6" sx={{ color: tokens.cream, mt: 1 }}>
                        {subscription?.isTrialActive && trialEnd ? format(trialEnd, 'PPP') : 'Not on trial'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, p: 2.5, borderRadius: 4, border: `1px solid ${tokens.border}`, bgcolor: 'rgba(255,255,255,0.02)' }}>
                  <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1.5 }}>
                    Subscription audit
                  </Typography>
                  <Grid container spacing={1.5}>
                    {auditRows.map(([label, value]) => (
                      <Grid item xs={12} sm={6} key={label}>
                        <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block' }}>{label}</Typography>
                        <Typography variant="body2" sx={{ color: tokens.cream, textTransform: label.includes('status') || label === 'Trial' || label === 'Entitlement' ? 'capitalize' : 'none' }}>
                          {value}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography variant="h5" sx={{ color: tokens.cream, mb: 1.5 }}>
                  Manage your app subscriptions
                </Typography>
                <Typography sx={{ color: tokens.textSoft, mb: 4 }}>
                  Purchases are managed securely through your mobile app store. Updates there sync back to FastTime automatically.
                </Typography>
                <Stack spacing={2}>
                  <Button
                    variant="outlined"
                    startIcon={<PhoneAndroid fontSize="small" />}
                    href="https://play.google.com/store"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open Google Play
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Apple fontSize="small" />}
                    href="https://apps.apple.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open App Store
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
